import { create } from 'zustand';
import type { LibraryFile, LibraryModel } from '@/types/library';
import { parseModelName } from '@/types/library';
import {
  deleteSize as apiDeleteSize,
  fetchLibrary,
  fetchSizeFiles,
  saveToLibrary,
} from '@/lib/api/libraryApi';
import { groupFilesByBaseName, processFileGroup } from '@/lib/batchProcessFiles';

export interface BatchItemStatus {
  baseName: string;
  fileCount: number;
  status: 'pending' | 'processing' | 'saved' | 'error';
  message?: string;
}

interface LibraryState {
  models: LibraryModel[];
  isLoading: boolean;
  loadError: string | null;

  selectedModelId: string | null;
  selectedSizeId: string | null;
  selectedSizeFiles: LibraryFile[] | null;
  isLoadingFiles: boolean;

  batchItems: BatchItemStatus[];
  isBatchRunning: boolean;

  loadLibrary: () => Promise<void>;
  selectModel: (modelId: string | null) => void;
  selectSize: (modelId: string, sizeId: string) => Promise<void>;
  clearSizeSelection: () => void;
  removeSize: (modelId: string, sizeId: string) => Promise<void>;
  runBatchImport: (files: File[]) => Promise<void>;
}

export const useLibraryStore = create<LibraryState>((set, get) => ({
  models: [],
  isLoading: false,
  loadError: null,

  selectedModelId: null,
  selectedSizeId: null,
  selectedSizeFiles: null,
  isLoadingFiles: false,

  batchItems: [],
  isBatchRunning: false,

  loadLibrary: async () => {
    set({ isLoading: true, loadError: null });
    try {
      const models = await fetchLibrary();
      set({ models, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load the library.';
      set({ isLoading: false, loadError: message });
    }
  },

  selectModel: (modelId) => {
    set({ selectedModelId: modelId, selectedSizeId: null, selectedSizeFiles: null });
  },

  selectSize: async (modelId, sizeId) => {
    set({ selectedModelId: modelId, selectedSizeId: sizeId, isLoadingFiles: true, selectedSizeFiles: null });
    try {
      const files = await fetchSizeFiles(modelId, sizeId);
      set({ selectedSizeFiles: files, isLoadingFiles: false });
    } catch (err) {
      set({ isLoadingFiles: false, selectedSizeFiles: [] });
    }
  },

  clearSizeSelection: () => set({ selectedSizeId: null, selectedSizeFiles: null }),

  removeSize: async (modelId, sizeId) => {
    await apiDeleteSize(modelId, sizeId);
    if (get().selectedSizeId === sizeId) {
      set({ selectedSizeId: null, selectedSizeFiles: null });
    }
    await get().loadLibrary();
  },

  runBatchImport: async (files: File[]) => {
    const groups = groupFilesByBaseName(files);
    set({
      isBatchRunning: true,
      batchItems: groups.map(
        (g): BatchItemStatus => ({ baseName: g.baseName, fileCount: g.files.length, status: 'pending' }),
      ),
    });

    const setItemStatus = (baseName: string, status: BatchItemStatus['status'], message?: string) => {
      set((s) => ({
        batchItems: s.batchItems.map((item) =>
          item.baseName === baseName ? { ...item, status, message } : item,
        ),
      }));
    };

    for (const group of groups) {
      setItemStatus(group.baseName, 'processing');

      const result = await processFileGroup(group);

      if (!result.ok) {
        setItemStatus(group.baseName, 'error', result.error);
        continue;
      }

      try {
        const { modelName, sizeLabel } = parseModelName(group.baseName);
        const { metrics, trim } = result.data;
        await saveToLibrary({
          modelName,
          sizeLabel,
          sourceFormat: result.data.sourceFormat,
          lengthMm: metrics.length,
          barrelDiameterMm: metrics.barrelDiameter,
          handleDiameterMm: metrics.handleDiameter,
          knobDiameterMm: metrics.knobDiameter,
          profilePoints: metrics.totalProfilePoints,
          autoTrimConfidence: trim.confidence,
          files: group.files,
        });
        setItemStatus(group.baseName, 'saved');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save to the library.';
        setItemStatus(group.baseName, 'error', message);
      }
    }

    set({ isBatchRunning: false });
    await get().loadLibrary();
  },
}));
